import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { PROVIDER_LABELS } from "../../lib/archModel";

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, {
  maximumFractionDigits: 2,
})}`;
const PROVIDERS = ["azure", "aws", "gcp"];

export default function ProviderComparisonDialog({ open, onClose, projectId,
                                                  currentProvider, dirty = false }) {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let active = true;
    setComparison(null);
    setError("");
    setLoading(true);
    api.post(`/projects/${projectId}/cost-comparison`)
      .then(({ data }) => {
        if (!active) return;
        if (!Array.isArray(data?.per_provider) || data.per_provider.length !== 3 ||
            !PROVIDERS.every((provider) => data.per_provider.some((row) => row.provider === provider))) {
          throw new Error("Comparison must include Azure, AWS, and GCP. Try again.");
        }
        setComparison(data);
      })
      .catch((reason) => {
        if (active) setError(reason.response?.data?.detail
          ? formatApiErrorDetail(reason.response.data.detail)
          : reason.message || "Comparison failed");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, projectId, currentProvider, attempt]);

  const lowest = comparison ? Math.min(...comparison.per_provider.map((row) => row.total)) : null;
  const leaders = comparison?.per_provider.filter((row) => row.total === lowest) || [];
  const currentTotal = comparison?.per_provider.find((row) => row.is_current)?.total;

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose?.(); }}>
      <DialogContent data-testid="provider-comparison-dialog"
        style={{ background: "var(--color-background-primary)",
          border: "1px solid var(--color-border-primary)",
          color: "var(--color-text-primary)", maxWidth: 920, maxHeight: "85vh",
          overflowY: "auto" }}>
        <DialogHeader>
          <DialogTitle>Compare cloud costs</DialogTitle>
          <DialogDescription style={{ color: "var(--color-text-secondary)" }}>
            Side-by-side estimates for the current saved architecture. The current
            provider uses its saved costs; other providers are fresh estimates,
            not historical versions or quotes from a cloud provider.
          </DialogDescription>
        </DialogHeader>
        {dirty && <p style={{ color: "var(--color-warning, #f59e0b)", fontSize: 12 }}>
          Unsaved canvas edits are not included. Save changes before comparing.
        </p>}
        {loading && <p className="flex items-center gap-2" role="status"
          style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>
          <Loader2 size={14} className="animate-spin" /> Pricing providers…
        </p>}
        {error && <div role="alert" style={{ color: "var(--color-danger)", fontSize: 13 }}>
          {error} <button type="button" onClick={retry} className="underline"
            style={{ marginLeft: 8 }}>Try again</button>
        </div>}
        {comparison && <div role="status" data-testid="compare-lowest"
          style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
          {leaders.length > 1 ? "Joint lowest monthly estimate" : "Lowest monthly estimate"}: {leaders.map(
            (row) => PROVIDER_LABELS[row.provider] || row.provider.toUpperCase()).join(" & ")} — {money(lowest)}/mo
        </div>}
        {comparison && <div style={{ display: "grid", gap: 12,
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {comparison.per_provider.map((row) => (
            <section key={row.provider} data-testid={`compare-provider-${row.provider}`}
              style={{ border: "1px solid var(--color-border-primary)", borderRadius: 10,
                background: "var(--color-background-secondary)", padding: 14 }}>
              <div className="flex items-center justify-between gap-2">
                <h3 style={{ fontWeight: 600, fontSize: 14 }}>
                  {PROVIDER_LABELS[row.provider] || row.provider.toUpperCase()}
                </h3>
                {row.is_current && <span className="font-mono"
                  style={{ fontSize: 10, color: "var(--color-accent)" }}>CURRENT</span>}
                {row.total === lowest && <span className="font-mono"
                  style={{ fontSize: 10, color: "var(--color-accent)" }}>LOWEST ESTIMATE</span>}
              </div>
              <p style={{ fontSize: 25, fontWeight: 700, marginTop: 10 }}>
                {money(row.total)}<span style={{ fontSize: 12, fontWeight: 400,
                  color: "var(--color-text-tertiary)" }}>/mo</span>
              </p>
              <p style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 3 }}>
                {row.nodes.length} matched resources · {row.is_current ? "saved" : "estimated"}
              </p>
              {!row.is_current && Number.isFinite(currentTotal) && <p style={{ fontSize: 11,
                color: "var(--color-text-secondary)", marginTop: 4 }}>
                {row.total === currentTotal ? "Same monthly estimate as current" :
                  `${money(Math.abs(row.total - currentTotal))}/mo ${row.total < currentTotal ?
                    "less" : "more"} than current`}
              </p>}
              <h4 style={{ fontSize: 11, fontWeight: 600, marginTop: 16, marginBottom: 7 }}>
                Major resources
              </h4>
              <ul style={{ display: "grid", gap: 8 }}>
                {[...row.nodes].sort((a, b) => b.monthly_cost_usd - a.monthly_cost_usd)
                  .slice(0, 5).map((node) => (
                    <li key={node.id} className="flex items-start justify-between gap-2"
                      style={{ fontSize: 11 }}>
                      <span><span style={{ fontWeight: 500 }}>{node.label}</span><br />
                        <span style={{ color: "var(--color-text-tertiary)" }}>
                          {node.provider_service}</span></span>
                      <span className="font-mono">{money(node.monthly_cost_usd)}</span>
                    </li>
                  ))}
              </ul>
            </section>
          ))}
        </div>}
        {comparison && <p style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
          Directional AI estimates only, not provider quotes. Regions, usage, data transfer,
          support, discounts, and migration effort can change the ranking. Validate with each
          provider&apos;s calculator before deciding.
        </p>}
      </DialogContent>
    </Dialog>
  );
}
